import React from 'react';
import { Check, Star, Users, Stethoscope } from 'lucide-react';

export function PlanCard({ 
  plan, 
  onSelect, 
  isSelected, 
  isLoading,
  currentPlanId 
}) {
  const isCurrentPlan = currentPlanId === plan.id;
  
  return (
    <div
      className={`
        relative bg-white rounded-2xl p-8 transition-all duration-300
        ${plan.is_popular 
          ? 'border-2 border-cyan-500 shadow-xl shadow-cyan-500/20 scale-105' 
          : 'border border-slate-200 shadow-lg hover:shadow-xl hover:border-cyan-300'
        }
        ${isSelected ? 'ring-2 ring-cyan-500' : ''}
      `}
    >
      {/* Popular Badge */}
      {plan.is_popular && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2">
          <div className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-4 py-1 rounded-full text-sm font-semibold flex items-center gap-1 shadow-lg">
            <Star size={14} fill="currentColor" />
            Most Popular
          </div>
        </div>
      )}

      {/* Current Plan Badge */}
      {isCurrentPlan && (
        <div className="absolute -top-4 right-4">
          <div className="bg-green-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
            Current Plan
          </div>
        </div>
      )}

      {/* Plan Header */}
      <div className="text-center mb-6">
        <h3 className="text-2xl font-bold text-slate-900">{plan.name}</h3>
        <p className="text-slate-500 mt-1">{plan.display_name}</p>
      </div>

      {/* Price */}
      <div className="text-center mb-6">
        <div className="flex items-baseline justify-center">
          <span className="text-xl font-semibold text-slate-600">₹</span>
          <span className="text-5xl font-bold text-slate-900">{plan.price}</span>
          <span className="text-slate-500 ml-2">
            /{plan.billing_cycle === 'annual' ? 'year' : 'month'}
          </span>
        </div>
        {plan.savings && (
          <div className="mt-2 inline-block bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-medium">
            Save ₹{plan.savings}
          </div>
        )}
      </div>

      {/* Key Features */}
      <div className="flex justify-center gap-6 mb-6 py-4 border-t border-b border-slate-100">
        <div className="flex items-center gap-2">
          <Users className="text-cyan-500" size={20} />
          <span className="text-slate-700 font-medium">
            {plan.max_patients} Patients
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Stethoscope 
            className={plan.nurse_access ? 'text-green-500' : 'text-slate-400'} 
            size={20} 
          />
          <span className={plan.nurse_access ? 'text-slate-700 font-medium' : 'text-slate-400'}>
            {plan.nurse_access ? 'Nurse Access' : 'No Nurse'}
          </span>
        </div>
      </div>

      {/* Features List */}
      <ul className="space-y-3 mb-8">
        {plan.features.slice(0, 6).map((feature, index) => (
          <li key={index} className="flex items-start gap-3">
            <Check className="text-green-500 flex-shrink-0 mt-0.5" size={18} />
            <span className="text-slate-600">{feature}</span>
          </li>
        ))}
        {plan.features.length > 6 && (
          <li className="text-cyan-500 text-sm font-medium text-center">
            +{plan.features.length - 6} more features
          </li>
        )}
      </ul>

      {/* CTA Button */}
      <button
        onClick={() => onSelect?.(plan.id)}
        disabled={isLoading || isCurrentPlan}
        className={`
          w-full py-4 rounded-xl font-semibold text-lg transition-all
          ${plan.is_popular
            ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white hover:from-cyan-600 hover:to-blue-600 shadow-lg shadow-cyan-500/30'
            : 'bg-slate-100 text-slate-900 hover:bg-slate-200'
          }
          ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}
          ${isCurrentPlan ? 'bg-green-100 text-green-700 cursor-default' : ''}
        `}
      >
        {isLoading ? (
          <span className="flex items-center justify-center gap-2">
            <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
              <circle 
                className="opacity-25" 
                cx="12" cy="12" r="10" 
                stroke="currentColor" 
                strokeWidth="4" 
                fill="none" 
              />
              <path 
                className="opacity-75" 
                fill="currentColor" 
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" 
              />
            </svg>
            Processing...
          </span>
        ) : isCurrentPlan ? (
          'Current Plan'
        ) : (
          'Get Started'
        )}
      </button>
    </div>
  );
}

export default PlanCard;
